import { ContextTracker, ExternalTokenizer, LRParser } from '@lezer/lr';
import { styleTags, tags } from '@lezer/highlight';
import { parseMixed } from '@lezer/common';
import { cssLanguage, css } from '@codemirror/lang-css';
import { typescriptLanguage, jsxLanguage, tsxLanguage, javascriptLanguage, javascript } from '@codemirror/lang-javascript';
import { EditorView } from '@codemirror/view';
import { EditorSelection } from '@codemirror/state';
import { syntaxTree, LRLanguage, indentNodeProp, foldNodeProp, LanguageSupport } from '@codemirror/language';

// This file was generated by lezer-generator. You probably shouldn't edit it.
const scriptText = 55,
	StartCloseScriptTag = 1,
	styleText = 56,
	StartCloseStyleTag = 2,
	textareaText = 57,
	StartCloseTextareaTag = 3,
	StartTag = 4,
	StartScriptTag = 5,
	StartStyleTag = 6,
	StartTextareaTag = 7,
	StartSelfClosingTag = 8,
	StartCloseTag = 9,
	NoMatchStartCloseTag = 10,
	MismatchedStartCloseTag = 11,
	missingCloseTag = 58,
	IncompleteCloseTag = 12,
	commentContent$1 = 59,
	singleBrace = 60,
	Element = 19,
	TagName = 21,
	Attribute = 22,
	AttributeName = 23,
	AttributeValue = 25,
	UnquotedAttributeValue = 26,
	ScriptText = 28,
	StyleText = 31,
	TextareaText = 34,
	OpenTag = 36,
	CloseTag = 37,
	Dialect_noMatch = 0;

/* Hand-written tokenizers for HTML. */

const selfClosers = {
	area: true,
	base: true,
	br: true,
	col: true,
	command: true,
	embed: true,
	frame: true,
	hr: true,
	img: true,
	input: true,
	keygen: true,
	link: true,
	meta: true,
	param: true,
	source: true,
	track: true,
	wbr: true,
	menuitem: true,
};

const implicitlyClosed = {
	dd: true,
	li: true,
	optgroup: true,
	option: true,
	p: true,
	rp: true,
	rt: true,
	tbody: true,
	td: true,
	tfoot: true,
	th: true,
	tr: true,
};

const closeOnOpen = {
	dd: { dd: true, dt: true },
	dt: { dd: true, dt: true },
	li: { li: true },
	option: { option: true, optgroup: true },
	optgroup: { optgroup: true },
	p: {
		address: true,
		article: true,
		aside: true,
		blockquote: true,
		dir: true,
		div: true,
		dl: true,
		fieldset: true,
		footer: true,
		form: true,
		h1: true,
		h2: true,
		h3: true,
		h4: true,
		h5: true,
		h6: true,
		header: true,
		hgroup: true,
		hr: true,
		menu: true,
		nav: true,
		ol: true,
		p: true,
		pre: true,
		section: true,
		table: true,
		ul: true,
	},
	rp: { rp: true, rt: true },
	rt: { rp: true, rt: true },
	tbody: { tbody: true, tfoot: true },
	td: { td: true, th: true },
	tfoot: { tbody: true },
	th: { td: true, th: true },
	thead: { tbody: true, tfoot: true },
	tr: { tr: true },
};

function nameChar(ch) {
	return (
		ch == 45 ||
		ch == 46 ||
		ch == 58 ||
		(ch >= 65 && ch <= 90) ||
		ch == 95 ||
		(ch >= 97 && ch <= 122) ||
		ch >= 161
	);
}

function isSpace(ch) {
	return ch == 9 || ch == 10 || ch == 13 || ch == 32;
}

let cachedName = null,
	cachedInput = null,
	cachedPos = 0;
function tagNameAfter(input, offset) {
	let pos = input.pos + offset;
	if (cachedPos == pos && cachedInput == input) return cachedName;
	let next = input.peek(offset);
	while (isSpace(next)) next = input.peek(++offset);
	let name = '';
	for (;;) {
		if (!nameChar(next)) break;
		name += String.fromCharCode(next);
		next = input.peek(++offset);
	}
	// Undefined to signal there's a <? or <!, null for just missing
	cachedInput = input;
	cachedPos = pos;
	return (cachedName = name
		? name.toLowerCase()
		: next == question || next == bang
			? undefined
			: null);
}

const lessThan = 60,
	greaterThan = 62,
	slash = 47,
	question = 63,
	bang = 33,
	dash = 45;

function ElementContext(name, parent) {
	this.name = name;
	this.parent = parent;
	this.hash = parent ? parent.hash : 0;
	for (let i = 0; i < name.length; i++)
		this.hash += (this.hash << 4) + name.charCodeAt(i) + (name.charCodeAt(i) << 8);
}

const startTagTerms = [
	StartTag,
	StartSelfClosingTag,
	StartScriptTag,
	StartStyleTag,
	StartTextareaTag,
];

const elementContext = /*@__PURE__*/new ContextTracker({
	start: null,
	shift(context, term, stack, input) {
		return startTagTerms.indexOf(term) > -1
			? new ElementContext(tagNameAfter(input, 1) || '', context)
			: context;
	},
	reduce(context, term) {
		return term == Element && context ? context.parent : context;
	},
	reuse(context, node, stack, input) {
		let type = node.type.id;
		return type == StartTag || type == OpenTag
			? new ElementContext(tagNameAfter(input, 1) || '', context)
			: context;
	},
	hash(context) {
		return context ? context.hash : 0;
	},
	strict: false,
});

const tagStart = /*@__PURE__*/new ExternalTokenizer(
	(input, stack) => {
		if (input.next != lessThan) {
			// End of file, close any open tags
			if (input.next < 0 && stack.context) input.acceptToken(missingCloseTag);
			return;
		}
		input.advance();
		let close = input.next == slash;
		if (close) input.advance();
		let name = tagNameAfter(input, 0);
		if (name === undefined) return;
		if (!name) return input.acceptToken(close ? IncompleteCloseTag : StartTag);

		let parent = stack.context ? stack.context.name : null;
		if (close) {
			if (name == parent) return input.acceptToken(StartCloseTag);
			if (parent && implicitlyClosed[parent]) return input.acceptToken(missingCloseTag, -2);
			if (stack.dialectEnabled(Dialect_noMatch)) return input.acceptToken(NoMatchStartCloseTag);
			for (let cx = stack.context; cx; cx = cx.parent) if (cx.name == name) return;
			input.acceptToken(MismatchedStartCloseTag);
		} else {
			if (name == 'script') return input.acceptToken(StartScriptTag);
			if (name == 'style') return input.acceptToken(StartStyleTag);
			if (name == 'textarea') return input.acceptToken(StartTextareaTag);
			if (selfClosers.hasOwnProperty(name)) return input.acceptToken(StartSelfClosingTag);
			if (parent && closeOnOpen[parent] && closeOnOpen[parent][name])
				input.acceptToken(missingCloseTag, -1);
			else input.acceptToken(StartTag);
		}
	},
	{ contextual: true },
);

const commentContent = /*@__PURE__*/new ExternalTokenizer((input) => {
	for (let dashes = 0, i = 0; ; i++) {
		if (input.next < 0) {
			if (i) input.acceptToken(commentContent$1);
			break;
		}
		if (input.next == dash) {
			dashes++;
		} else if (input.next == greaterThan && dashes >= 2) {
			if (i > 3) input.acceptToken(commentContent$1, -2);
			break;
		} else {
			dashes = 0;
		}
		input.advance();
	}
});

function contentTokenizer(tag, textToken, endToken) {
	let lastState = 2 + tag.length;
	return new ExternalTokenizer((input) => {
		// state means:
		// - 0 nothing matched
		// - 1 '<' matched
		// - 2 '</' + possibly whitespace matched
		// - 3-(1+tag.length) part of the tag matched
		// - lastState whole tag + possibly whitespace matched
		for (let state = 0, matchedLen = 0, i = 0; ; i++) {
			if (input.next < 0) {
				if (i) input.acceptToken(textToken);
				break;
			}
			if (
				(state == 0 && input.next == lessThan) ||
				(state == 1 && input.next == slash) ||
				(state >= 2 && state < lastState && input.next == tag.charCodeAt(state - 2))
			) {
				state++;
				matchedLen++;
			} else if ((state == 2 || state == lastState) && isSpace(input.next)) {
				matchedLen++;
			} else if (state == lastState && input.next == greaterThan) {
				if (i > matchedLen) input.acceptToken(textToken, -matchedLen);
				else input.acceptToken(endToken, -(matchedLen - 2));
				break;
			} else if ((input.next == 10 /* '\n' */ || input.next == 13) /* '\r' */ && i) {
				input.acceptToken(textToken, 1);
				break;
			} else {
				state = matchedLen = 0;
			}
			input.advance();
		}
	});
}

const scriptTokens = /*@__PURE__*/contentTokenizer('script', scriptText, StartCloseScriptTag);

const styleTokens = /*@__PURE__*/contentTokenizer('style', styleText, StartCloseStyleTag);

const textareaTokens = /*@__PURE__*/contentTokenizer('textarea', textareaText, StartCloseTextareaTag);

const openBrace = 123; // '{'

// Tokenizer for a single '{' that is NOT the start of a '{{' resolvable.
// This handles the edge case where '{' appears at the end of an attribute value
// (e.g., attr="{" or attr="a{") which can't be matched by grammar-only patterns.
const singleBraceToken = /*@__PURE__*/new ExternalTokenizer((input) => {
	if (input.next === openBrace) {
		input.advance();
		// Only accept if NOT followed by another '{' (which would start a resolvable)
		if (input.next !== openBrace) {
			input.acceptToken(singleBrace);
		}
	}
});

const htmlHighlighting = /*@__PURE__*/styleTags({
	'Text RawText': tags.content,
	'StartTag StartCloseTag SelfClosingEndTag EndTag': tags.angleBracket,
	TagName: tags.tagName,
	'MismatchedCloseTag/TagName': [tags.tagName, tags.invalid],
	AttributeName: tags.attributeName,
	'AttributeValue UnquotedAttributeValue': tags.attributeValue,
	Is: tags.definitionOperator,
	'EntityReference CharacterReference': tags.character,
	Comment: tags.blockComment,
	ProcessingInst: tags.processingInstruction,
	DoctypeDecl: tags.documentMeta,
});

// This file was generated by lezer-generator. You probably shouldn't edit it.
const parser = /*@__PURE__*/LRParser.deserialize({
	version: 14,
	states:
		",xOVO!jOOO!ZQ#tO'#CpO!`Q#tO'#CzO!eQ#tO'#C}O!jQ#tO'#DQO!oQ#tO'#DSO!tOXO'#CoO#POYO'#CoO#[O[O'#CoO$kO!jO'#CoOOOW'#Co'#CoO$rO$UO'#DUO$zQ#tO'#DWO%PQ#tO'#DXOOOW'#Dm'#DmOOOW'#DZ'#DZQVO!jOOO%UQ&jO,59[O%^Q&jO,59fO%fQ&jO,59iO%nQ&jO,59lO%yQ&jO,59nOOOX'#D_'#D_O&ROXO'#CxO&^OXO,59ZOOOY'#D`'#D`O&fOYO'#C{O&qOYO,59ZOOO['#Da'#DaO&yO[O'#DOO'UO[O,59ZOOOW'#Db'#DbO'^O!jO,59ZO'eQ#tO'#DROOOW,59Z,59ZOOO`'#Dc'#DcO'jO$UO,59pOOOW,59p,59pO'rQ#tO,59rO'wQ#tO,59sOOOW-E7X-E7XO'|Q&jO'#CrOOQO'#D['#D[O([Q&jO1G.vOOOX1G.v1G.vO(dQ&jO1G/QOOOY1G/Q1G/QO(lQ&jO1G/TOOO[1G/T1G/TO(tQ&jO1G/WOOOW1G/W1G/WOOOW1G/Y1G/YO)PQ&jO1G/YOOOX-E7]-E7]O)XQ#tO'#CyOOOW1G.u1G.uOOOY-E7^-E7^O)^Q#tO'#C|OOO[-E7_-E7_O)cQ#tO'#DPOOOW-E7`-E7`O)hQ#tO,59mOOO`-E7a-E7aOOOW1G/[1G/[OOOW1G/^1G/^OOOW1G/_1G/_O)mQ,UO,59^OOQO-E7Y-E7YOOOX7+$b7+$bOOOY7+$l7+$lOOO[7+$o7+$oOOOW7+$r7+$rOOOW7+$t7+$tO)xQ#tO,59eO)}Q#tO,59hO*SQ#tO,59kOOOW1G/X1G/XO*mO7|O'#CuO+YONYO'#CuOOQO1G.x1G.xOOOW1G/P1G/POOOW1G/S1G/SOOOW1G/V1G/VOOOp'#D]'#D]O+aO7|O,59aOOQO,59a,59aOOOp'#D^'#D^O+hONYO,59aOOOp-E7Z-E7ZOOQO1G.{1G.{OOOp-E7[-E7[",
	stateData:
		',O~O!`OS~OSSOTPOUQOVROWTOY]OZ[O[^O^^O_^O`^Oa^Ob^Oy^O|_O!fZO~OeaO~OebO~OecO~OedO~OeeO~O!XfOPlP![lP~O!YiOQoP![oP~O!ZlORrP![rP~OSSOTPOUQOVROWTOXqOY]OZ[O[^O^^O_^O`^Oa^Ob^Oy^O!fZO~O![rO~P#gO!]sO!guO~OevO~OewO~OgyOk|O~OgyOk!OO~OgyOk!QO~OgyOk!SOw!TO~OgyOk!TO~O!XfOPlX![lX~OP!WO![!XO~O!YiOQoX![oX~OQ!ZO![!XO~O!ZlORrX![rX~OR!]O![!XO~O![!XO~P#gOe!_O~O!]sO!g!aO~Ok!bO~Ok!cO~Oh!dOgfXkfXwfX~OgyOk!fO~OgyOk!gO~OgyOk!hO~OgyOk!iOw!jO~OgyOk!jO~Oe!kO~Oe!lO~Oe!mO~Ok!nO~Oj!qO!b!oO!d!pO~Ok!rO~Ok!sO~Ok!tO~O_!uO`!uOa!uOb!uO!^!uO!c!uO~O!b!wO~P*XO_!xO`!xOa!xOb!xO!^!xO!e!xO~O!d!wO~P*tO!b!{O~P*XO!d!{O~P*tOw~_^w!fy|a`!c!ebk~',
	goto: '%r!bPPPPPPPPPPPPPPPPPPP!c!iP!oPP!{PP#O#R#U#[#_#b#h#k#n#t#zP!cP!c!cP$Q$W$n$t$z%Q%W%^%dPPPPPPPPP%jX^OX`pXUOX`pezabcde{}!P!R!UR!q!dRhUR!XhXVOX`pRkVR!XkXWOX`pRnWR!XnXXOX`pQrXR!XpXYOX`pQ`ORx`Q{aQ}bQ!PcQ!RdQ!UeZ!e{}!P!R!UQ!v!oR!z!vQ!y!pR!|!yQgUR!VgQjVR!YjQmWR![mQpXR!^pQtZR!`tS_O`ToXp',
	nodeNames:
		'⚠ StartCloseTag StartCloseTag StartCloseTag StartTag StartTag StartTag StartTag StartTag StartCloseTag StartCloseTag StartCloseTag IncompleteCloseTag Document Text Resolvable EntityReference CharacterReference InvalidEntity Element OpenTag TagName Attribute AttributeName Is AttributeValue UnquotedAttributeValue EndTag ScriptText CloseTag OpenTag StyleText CloseTag OpenTag TextareaText CloseTag OpenTag CloseTag SelfClosingTag SelfClosingEndTag Comment ProcessingInst MismatchedCloseTag CloseTag DoctypeDecl',
	maxTerm: 69,
	context: elementContext,
	nodeProps: [
		[
			'closedBy',
			-10,
			1,
			2,
			3,
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			'EndTag',
			4,
			'EndTag SelfClosingEndTag',
			-4,
			20,
			30,
			33,
			36,
			'CloseTag',
		],
		[
			'group',
			-10,
			12,
			15,
			16,
			17,
			18,
			19,
			40,
			41,
			42,
			43,
			'Entity',
			14,
			'Entity TextContent',
			-3,
			28,
			31,
			34,
			'TextContent Entity',
		],
		['openedBy', 27, 'StartTag StartCloseTag', -4, 29, 32, 35, 37, 'OpenTag', 39, 'StartTag'],
	],
	propSources: [htmlHighlighting],
	skippedNodes: [0],
	repeatNodeCount: 9,
	tokenData:
		"$=^!aR!ZOX$tXY0tYZ0tZ[$t[]&b]^0t^p$tpq0tqr2XrsAosv2XvwBewx!)Yx}2X}!O!*R!O!P2X!P!Q!/d!Q![2X![!]!2T!]!^2X!^!_!6`!_!`#8y!`!a#9w!a!c2X!c!}!2T!}#R2X#R#S!2T#S#T8]#T#o!2T#o#p#:u#p#s2X#s$f$t$f%W2X%W%o!2T%o%p2X%p&a!2T&a&b2X&b1p!2T1p4U2X4U4d!2T4d4e2X4e$IS!2T$IS$I`2X$I`$Ib!2T$Ib$Kh2X$Kh%#t!2T%#t&/x2X&/x&Et!2T&Et&FV2X&FV;'S!2T;'S;:j!6Y;:j;=`Ai<%l?&r2X?&r?Ah!2T?Ah?BY$t?BY?Mn!2T?MnO$t!Z%PejW^P!c`!epOX$tXZ&bZ[$t[^&b^p$tpq&bqr$trs'^sv$tvw,mwx*Ux!^$t!^!_+p!_!a&b!a#S$t#S#T&b#T#o$t#o#p-h#p;'S$t;'S;=`0n<%lO$t!R&kZ^P!c`!epOr&brs'^sv&bwx*Ux!^&b!^!_+p!_#o&b#o#p(l#p;'S&b;'S;=`,g<%lO&bq'eX^P!epOv'^wx(Qx!^'^!^!_)d!_#o'^#o#p(l#p;'S'^;'S;=`*O<%lO'^P(VV^POv(Qw!^(Q!_#o(Q#o#p(l#p;'S(Q;'S;=`)X<%lO(QP(oWOv(Qw!^(Q!_#o(Q#p;'S(Q;'S;=`)X<%l~(Q~O(Q~~)_P)[P;=`<%l(QP)dO^Pp)iT!epOv)dx#o)d#p;'S)d;'S;=`)x<%lO)dp){P;=`<%l)dq*RP;=`<%l'^a*]Y^P!c`Or*Urs(Qsv*Uw!^*U!^!_*{!_#o*U#o#p(l#p;'S*U;'S;=`+j<%lO*U`+QU!c`Or*{sv*{w#o*{#p;'S*{;'S;=`+d<%lO*{`+gP;=`<%l*{a+mP;=`<%l*U!Q+wW!c`!epOr+prs)dsv+pwx*{x#o+p#p;'S+p;'S;=`,a<%lO+p!Q,dP;=`<%l+p!R,jP;=`<%l&bW,rYjWOX,mZ[,m^p,mqr,msw,mx!^,m!a#S,m#T;'S,m;'S;=`-b<%lO,mW-eP;=`<%l,mX-mfjWOX/RXZ(QZ[/R[^(Q^p/Rpq(Qqr/Rrs(Qsv/Rvw,mwx(Qx!^/R!_!a(Q!a#S/R#S#T(Q#T#o/R#o#p,m#p;'S/R;'S;=`0h<%l~/R~O/R~~)_X/YdjW^POX/RXZ(QZ[/R[^(Q^p/Rpq(Qqr/Rrs(Qsv/Rvw,mwx(Qx!^/R!_!a(Q!a#S/R#S#T(Q#T#o/R#o#p-h#p;'S/R;'S;=`0h<%lO/RX0kP;=`<%l/R!Z0qP;=`<%l$t!a1Pb!`^^P!c`!epOX&bXY0tYZ0tZ]&b]^0t^p&bpq0tqr&brs'^sv&bwx*Ux!^&b!^!_+p!_#o&b#o#p(l#p;'S&b;'S;=`,g<%lO&b!_2flgSjW^P!c`!epOX$tXZ&bZ[$t[^&b^p$tpq&bqr2Xrs'^sv2Xvw4^wx*Ux!P2X!P!Q$t!Q!^2X!^!_6n!_!a&b!a#S2X#S#T8]#T#o2X#o#p=]#p#s2X#s$f$t$f;'S2X;'S;=`Ai<%l?Ah2X?Ah?BY$t?BY?Mn2X?MnO$t[4ecgSjWOX,mZ[,m^p,mqr4^sw4^x!P4^!P!Q,m!Q!^4^!^!_5p!a#S4^#S#T5p#T#s4^#s$f,m$f;'S4^;'S;=`6h<%l?Ah4^?Ah?BY,m?BY?Mn4^?MnO,mS5uXgSqr5psw5px!P5p!Q!_5p!a#s5p$f;'S5p;'S;=`6b<%l?Ah5p?BY?Mn5pS6eP;=`<%l5p[6kP;=`<%l4^!U6wdgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!U8YP;=`<%l6n!V8hegS^P!c`!epOq&bqr8]rs'^sv8]vw5pwx*Ux!P8]!P!Q&b!Q!^8]!^!_6n!_!a&b!a#o8]#o#p9y#p#s8]#s$f&b$f;'S8];'S;=`=V<%l?Ah8]?Ah?BY&b?BY?Mn8]?MnO&bT:OggSOq(Qqr;grs(Qsv;gvw5pwx(Qx!P;g!P!Q(Q!Q!^;g!^!_5p!_!a(Q!a#o;g#o#p5p#p#s;g#s$f(Q$f;'S;g;'S;=`=P<%l?Ah;g?Ah?BY(Q?BY?Mn;g?Mn~(Q~O(Q~~)_T;negS^POq(Qqr;grs(Qsv;gvw5pwx(Qx!P;g!P!Q(Q!Q!^;g!^!_5p!_!a(Q!a#o;g#o#p9y#p#s;g#s$f(Q$f;'S;g;'S;=`=P<%l?Ah;g?Ah?BY(Q?BY?Mn;g?MnO(QT=SP;=`<%l;g!V=YP;=`<%l8]]=dngSjWOX/RXZ(QZ[/R[^(Q^p/Rpq(Qqr?brs(Qsv?bvw4^wx(Qx!P?b!P!Q/R!Q!^?b!^!_5p!_!a(Q!a#S?b#S#T;g#T#o?b#o#p4^#p#s?b#s$f/R$f;'S?b;'S;=`Ac<%l?Ah?b?Ah?BY/R?BY?Mn?b?Mn~/R~O/R~~)_]?klgSjW^POX/RXZ(QZ[/R[^(Q^p/Rpq(Qqr?brs(Qsv?bvw4^wx(Qx!P?b!P!Q/R!Q!^?b!^!_5p!_!a(Q!a#S?b#S#T;g#T#o?b#o#p=]#p#s?b#s$f/R$f;'S?b;'S;=`Ac<%l?Ah?b?Ah?BY/R?BY?Mn?b?MnO/R]AfP;=`<%l?b!_AlP;=`<%l2X!ZAxX!bh^P!epOv'^wx(Qx!^'^!^!_)d!_#o'^#o#p(l#p;'S'^;'S;=`*O<%lO'^!_BnjgSjWb!ROXD`XZEmZ[D`[^Em^pD`qrG_rsEmstMPtwG_wxEmx!PG_!P!QD`!Q!]G_!]!^4^!^!_Jl!_!aEm!a#SG_#S#TJl#T#sG_#s$fD`$f;'SG_;'S;=`Ly<%l?AhG_?Ah?BYD`?BY?MnG_?MnOD`!ZDebjWOXD`XZEmZ[D`[^Em^pD`qrD`rsEmst,mtwD`wxEmx!]D`!]!^Fb!^!aEm!a#SD`#S#TEm#T;'SD`;'S;=`GX<%lOD`!REpVOpEmqsEmt!]Em!]!^FV!^;'SEm;'S;=`F[<%lOEm!RF[O`!R!RF_P;=`<%lEm!ZFiYjW`!ROX,mZ[,m^p,mqr,msw,mx!^,m!a#S,m#T;'S,m;'S;=`-b<%lO,m!ZG[P;=`<%lD`!_GfjgSjWOXD`XZEmZ[D`[^Em^pD`qrG_rsEmst4^twG_wxEmx!PG_!P!QD`!Q!]G_!]!^IW!^!_Jl!_!aEm!a#SG_#S#TJl#T#sG_#s$fD`$f;'SG_;'S;=`Ly<%l?AhG_?Ah?BYD`?BY?MnG_?MnOD`!_IacgSjW`!ROX,mZ[,m^p,mqr4^sw4^x!P4^!P!Q,m!Q!^4^!^!_5p!a#S4^#S#T5p#T#s4^#s$f,m$f;'S4^;'S;=`6h<%l?Ah4^?Ah?BY,m?BY?Mn4^?MnO,m!VJqdgSOpEmqrJlrsEmst5ptwJlwxEmx!PJl!P!QEm!Q!]Jl!]!^LP!^!_Jl!_!aEm!a#sJl#s$fEm$f;'SJl;'S;=`Ls<%l?AhJl?Ah?BYEm?BY?MnJl?MnOEm!VLWXgS`!Rqr5psw5px!P5p!Q!_5p!a#s5p$f;'S5p;'S;=`6b<%l?Ah5p?BY?Mn5p!VLvP;=`<%lJl!_L|P;=`<%lG_!_MWigSjWOXNuXZ!!PZ[Nu[^!!P^pNuqr!#nrs!!Psw!#nwx!!Px!P!#n!P!QNu!Q!]!#n!]!^4^!^!_!&x!_!a!!P!a#S!#n#S#T!&x#T#s!#n#s$fNu$f;'S!#n;'S;=`!)S<%l?Ah!#n?Ah?BYNu?BY?Mn!#n?MnONu!ZNzajWOXNuXZ!!PZ[Nu[^!!P^pNuqrNurs!!PswNuwx!!Px!]Nu!]!^!!q!^!a!!P!a#SNu#S#T!!P#T;'SNu;'S;=`!#h<%lONu!R!!SUOp!!Pq!]!!P!]!^!!f!^;'S!!P;'S;=`!!k<%lO!!P!R!!kOa!R!R!!nP;=`<%l!!P!Z!!xYjWa!ROX,mZ[,m^p,mqr,msw,mx!^,m!a#S,m#T;'S,m;'S;=`-b<%lO,m!Z!#kP;=`<%lNu!_!#uigSjWOXNuXZ!!PZ[Nu[^!!P^pNuqr!#nrs!!Psw!#nwx!!Px!P!#n!P!QNu!Q!]!#n!]!^!%d!^!_!&x!_!a!!P!a#S!#n#S#T!&x#T#s!#n#s$fNu$f;'S!#n;'S;=`!)S<%l?Ah!#n?Ah?BYNu?BY?Mn!#n?MnONu!_!%mcgSjWa!ROX,mZ[,m^p,mqr4^sw4^x!P4^!P!Q,m!Q!^4^!^!_5p!a#S4^#S#T5p#T#s4^#s$f,m$f;'S4^;'S;=`6h<%l?Ah4^?Ah?BY,m?BY?Mn4^?MnO,m!V!&}cgSOp!!Pqr!&xrs!!Psw!&xwx!!Px!P!&x!P!Q!!P!Q!]!&x!]!^!(Y!^!_!&x!_!a!!P!a#s!&x#s$f!!P$f;'S!&x;'S;=`!(|<%l?Ah!&x?Ah?BY!!P?BY?Mn!&x?MnO!!P!V!(aXgSa!Rqr5psw5px!P5p!Q!_5p!a#s5p$f;'S5p;'S;=`6b<%l?Ah5p?BY?Mn5p!V!)PP;=`<%l!&x!_!)VP;=`<%l!#n!Z!)cY!dx^P!c`Or*Urs(Qsv*Uw!^*U!^!_*{!_#o*U#o#p(l#p;'S*U;'S;=`+j<%lO*U!a!*`ngSjW^P!c`!epOX$tXZ&bZ[$t[^&b^p$tpq&bqr2Xrs'^sv2Xvw4^wx*Ux}2X}!O!,^!O!P2X!P!Q$t!Q!^2X!^!_6n!_!a&b!a#S2X#S#T8]#T#o2X#o#p=]#p#s2X#s$f$t$f;'S2X;'S;=`Ai<%l?Ah2X?Ah?BY$t?BY?Mn2X?MnO$t!a!,kmgSjW^P!c`!epOX$tXZ&bZ[$t[^&b^p$tpq&bqr2Xrs'^sv2Xvw4^wx*Ux!P2X!P!Q$t!Q!^2X!^!_6n!_!`&b!`!a!.f!a#S2X#S#T8]#T#o2X#o#p=]#p#s2X#s$f$t$f;'S2X;'S;=`Ai<%l?Ah2X?Ah?BY$t?BY?Mn2X?MnO$t!T!.qZ!gQ^P!c`!epOr&brs'^sv&bwx*Ux!^&b!^!_+p!_#o&b#o#p(l#p;'S&b;'S;=`,g<%lO&b!a!/ofjW^P!c`!epOX$tXZ&bZ[$t[^&b^p$tpq&bqr$trs'^sv$tvw,mwx*Ux!^$t!^!_+p!_!`&b!`!a!1T!a#S$t#S#T&b#T#o$t#o#p-h#p;'S$t;'S;=`0n<%lO$t!X!1bZ^PwS!c`!epkUOr&brs'^sv&bwx*Ux!^&b!^!_+p!_#o&b#o#p(l#p;'S&b;'S;=`,g<%lO&b!a!2d![gSeQjW^P!c`!epOX$tXZ&bZ[$t[^&b^p$tpq&bqr2Xrs'^sv2Xvw4^wx*Ux}2X}!O!2T!O!P!2T!P!Q$t!Q![!2T![!]!2T!]!^2X!^!_6n!_!a&b!a!c2X!c!}!2T!}#R2X#R#S!2T#S#T8]#T#o!2T#o#p=]#p#s2X#s$f$t$f$}2X$}%O!2T%O%W2X%W%o!2T%o%p2X%p&a!2T&a&b2X&b1p!2T1p4U!2T4U4d!2T4d4e2X4e$IS!2T$IS$I`2X$I`$Ib!2T$Ib$Je2X$Je$Jg!2T$Jg$Kh2X$Kh%#t!2T%#t&/x2X&/x&Et!2T&Et&FV2X&FV;'S!2T;'S;:j!6Y;:j;=`Ai<%l?&r2X?&r?Ah!2T?Ah?BY$t?BY?Mn!2T?MnO$t!a!6]P;=`<%l!2T!V!6iegS!c`!epOq+pqr!7zrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a!b#*x!b#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V!8TjgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x}6n}!O!9u!O!P6n!P!Q+p!Q!_6n!_!a+p!a!f6n!f!g!<}!g#W6n#W#X# S#X#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V!:OfgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x}6n}!O!;d!O!P6n!P!Q+p!Q!_6n!_!a+p!a#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V!;odgS!fP!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V!=WfgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a!q6n!q!r!>l!r#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V!>ufgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a!e6n!e!f!@Z!f#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V!@dfgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a!v6n!v!w!Ax!w#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V!BRfgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a!{6n!{!|!Cg!|#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V!CpfgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a!r6n!r!s!EU!s#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V!E_fgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a!g6n!g!h!Fs!h#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V!F|egS!c`!epOq!H_qr!Fsrs!I[sv!Fsvw!Miwx!KXx!P!Fs!P!Q!H_!Q!_!Fs!_!`!H_!`!a!Lp!a#o!Fs#o#p!Mi#p#s!Fs#s$f!H_$f;'S!Fs;'S;=`!N|<%l?Ah!Fs?Ah?BY!H_?BY?Mn!Fs?MnO!H_!R!Hf[!c`!epOr!H_rs!I[sv!H_vw!I|wx!KXx!`!H_!`!a!Lp!a#o!H_#o#p!I|#p;'S!H_;'S;=`!Mc<%lO!H_q!IaX!epOv!I[vx!I|x!`!I[!`!a!Jk!a#o!I[#o#p!I|#p;'S!I[;'S;=`!KR<%lO!I[P!JPTO!`!I|!`!a!J`!a;'S!I|;'S;=`!Je<%lO!I|P!JeO|PP!JhP;=`<%l!I|q!JrT|P!epOv)dx#o)d#p;'S)d;'S;=`)x<%lO)dq!KUP;=`<%l!I[a!K^Z!c`Or!KXrs!I|sv!KXvw!I|w!`!KX!`!a!LP!a#o!KX#o#p!I|#p;'S!KX;'S;=`!Lj<%lO!KXa!LWU|P!c`Or*{sv*{w#o*{#p;'S*{;'S;=`+d<%lO*{a!LmP;=`<%l!KX!R!LyW|P!c`!epOr+prs)dsv+pwx*{x#o+p#p;'S+p;'S;=`,a<%lO+p!R!MfP;=`<%l!H_T!MnbgSOq!I|qr!Mirs!I|sw!Miwx!I|x!P!Mi!P!Q!I|!Q!_!Mi!_!`!I|!`!a!J`!a#s!Mi#s$f!I|$f;'S!Mi;'S;=`!Nv<%l?Ah!Mi?Ah?BY!I|?BY?Mn!Mi?MnO!I|T!NyP;=`<%l!Mi!V# PP;=`<%l!Fs!V# ]fgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a#c6n#c#d#!q#d#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V#!zfgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a#V6n#V#W#$`#W#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V#$ifgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a#h6n#h#i#%}#i#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V#&WfgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a#m6n#m#n#'l#n#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V#'ufgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a#d6n#d#e#)Z#e#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V#)dfgS!c`!epOq+pqr6nrs)dsv6nvw5pwx*{x!P6n!P!Q+p!Q!_6n!_!a+p!a#X6n#X#Y!Fs#Y#o6n#o#p5p#p#s6n#s$f+p$f;'S6n;'S;=`8V<%l?Ah6n?Ah?BY+p?BY?Mn6n?MnO+p!V#+RegS!c`!epOq#,dqr#*xrs#-asv#*xvw#4gwx#0bx!P#*x!P!Q#,d!Q!_#*x!_!a#,d!a!b#7X!b#o#*x#o#p#4g#p#s#*x#s$f#,d$f;'S#*x;'S;=`#8s<%l?Ah#*x?Ah?BY#,d?BY?Mn#*x?MnO#,d!R#,k[!c`!epOr#,drs#-asv#,dvw#.Rwx#0bx!a#,d!a!b#2q!b#o#,d#o#p#.R#p;'S#,d;'S;=`#4a<%lO#,dq#-fX!epOv#-avx#.Rx!a#-a!a!b#/S!b#o#-a#o#p#.R#p;'S#-a;'S;=`#0[<%lO#-aP#.UTO!a#.R!a!b#.e!b;'S#.R;'S;=`#.|<%lO#.RP#.hTO!`#.R!`!a#.w!a;'S#.R;'S;=`#.|<%lO#.RP#.|OyPP#/PP;=`<%l#.Rq#/XX!epOv#-avx#.Rx!`#-a!`!a#/t!a#o#-a#o#p#.R#p;'S#-a;'S;=`#0[<%lO#-aq#/{TyP!epOv)dx#o)d#p;'S)d;'S;=`)x<%lO)dq#0_P;=`<%l#-aa#0gZ!c`Or#0brs#.Rsv#0bvw#.Rw!a#0b!a!b#1Y!b#o#0b#o#p#.R#p;'S#0b;'S;=`#2k<%lO#0ba#1_Z!c`Or#0brs#.Rsv#0bvw#.Rw!`#0b!`!a#2Q!a#o#0b#o#p#.R#p;'S#0b;'S;=`#2k<%lO#0ba#2XUyP!c`Or*{sv*{w#o*{#p;'S*{;'S;=`+d<%lO*{a#2nP;=`<%l#0b!R#2x[!c`!epOr#,drs#-asv#,dvw#.Rwx#0bx!`#,d!`!a#3n!a#o#,d#o#p#.R#p;'S#,d;'S;=`#4a<%lO#,d!R#3wWyP!c`!epOr+prs)dsv+pwx*{x#o+p#p;'S+p;'S;=`,a<%lO+p!R#4dP;=`<%l#,dT#4lbgSOq#.Rqr#4grs#.Rsw#4gwx#.Rx!P#4g!P!Q#.R!Q!_#4g!_!a#.R!a!b#5t!b#s#4g#s$f#.R$f;'S#4g;'S;=`#7R<%l?Ah#4g?Ah?BY#.R?BY?Mn#4g?MnO#.RT#5ybgSOq#.Rqr#4grs#.Rsw#4gwx#.Rx!P#4g!P!Q#.R!Q!_#4g!_!`#.R!`!a#.w!a#s#4g#s$f#.R$f;'S#4g;'S;=`#7R<%l?Ah#4g?Ah?BY#.R?BY?Mn#4g?MnO#.RT#7UP;=`<%l#4g!V#7begS!c`!epOq#,dqr#*xrs#-asv#*xvw#4gwx#0bx!P#*x!P!Q#,d!Q!_#*x!_!`#,d!`!a#3n!a#o#*x#o#p#4g#p#s#*x#s$f#,d$f;'S#*x;'S;=`#8s<%l?Ah#*x?Ah?BY#,d?BY?Mn#*x?MnO#,d!V#8vP;=`<%l#*x!V#9UZhS^P!c`!epOr&brs'^sv&bwx*Ux!^&b!^!_+p!_#o&b#o#p(l#p;'S&b;'S;=`,g<%lO&b!X#:SZ^P!c`!epkUOr&brs'^sv&bwx*Ux!^&b!^!_+p!_#o&b#o#p(l#p;'S&b;'S;=`,g<%lO&b!_#:|ngSjWOX/RXZ(QZ[/R[^(Q^p/Rpq(Qqr?brs(Qsv?bvw4^wx(Qx!P?b!P!Q/R!Q!^?b!^!_5p!_!a(Q!a#S?b#S#T;g#T#o?b#o#p#<z#p#s?b#s$f/R$f;'S?b;'S;=`Ac<%l?Ah?b?Ah?BY/R?BY?Mn?b?Mn~/R~O/R~~)_!_#=R!RgSjWOX#@[XZ#CWZ[#@[[^#CW^p#@[pq#CWqr#<zrs#CWsw#<zwx#CWx!P#<z!P!Q#@[!Q![#<z![!^#<z!^!_$$v!_!a#CW!a!c#<z!c!}#<z!}#O#<z#O#P$1W#P#S#<z#S#T$$v#T#o#<z#o#q#<z#q#r$9v#r#s#<z#s#y#@[#y#z#@[#z$f#@[$f$g#<z$gG|#<zG|#BY4^#BY#BZ#<z#BZ$IS4^$IS$I_#<z$I_$I|4^$I|$JO#<z$JO$JT4^$JT$JU#<z$JU$KV4^$KV$KW#<z$KW&FU4^&FU&FV#<z&FV;'S4^;'S;=`6h<%l?Ah4^?Ah?BY,m?BY?Mn4^?MnO,m!Z#@a{jWOX#@[XZ#CWZ[#@[[^#CW^p#@[pq#CWqr#@[rs#CWsw#@[wx#CWx!Q#@[!Q![#@[![!^#@[!^!a#CW!a!c#@[!c!}#@[!}#O#@[#O#P#I_#P#S#@[#S#T#CW#T#o#@[#o#q#@[#q#r$!i#r#y#@[#y#z#@[#z$f#@[$f$g#@[$gG|#@[G|#BY,m#BY#BZ#@[#BZ$IS,m$IS$I_#@[$I_$I|,m$I|$JO#@[$JO$JT,m$JT$JU#@[$JU$KV,m$KV$KW#@[$KW&FU,m&FU&FV#@[&FV;'S,m;'S;=`-b<%lO,m!R#CZiOX#CWX^#CW^p#CWpq#CWq!Q#CW!Q![#CW![!c#CW!c!}#CW!}#O#CW#O#P#Dx#P#T#CW#T#o#CW#o#q#CW#q#r#Hp#r#y#CW#y#z#CW#z$f#CW$f$g#CW$gG|#CW#BY#BZ#CW$IS$I_#CW$I|$JO#CW$JT$JU#CW$KV$KW#CW&FU&FV#CW!R#D{iOX#CWX^#CW^p#CWpq#CWq!Q#CW!Q![#CW![!c#CW!c!}#CW!}#O#CW#O#P#Dx#P#T#CW#T#o#CW#o#q#CW#q#r#Fj#r#y#CW#y#z#CW#z$f#CW$f$g#CW$gG|#CW#BY#BZ#CW$IS$I_#CW$I|$JO#CW$JT$JU#CW$KV$KW#CW&FU&FV#CW!R#FmTO#q#CW#q#r#F|#r;'S#CW;'S;=`#IX<%lO#CW!R#GRi_!ROX#CWX^#CW^p#CWpq#CWq!Q#CW!Q![#CW![!c#CW!c!}#CW!}#O#CW#O#P#Dx#P#T#CW#T#o#CW#o#q#CW#q#r#Hp#r#y#CW#y#z#CW#z$f#CW$f$g#CW$gG|#CW#BY#BZ#CW$IS$I_#CW$I|$JO#CW$JT$JU#CW$KV$KW#CW&FU&FV#CW!R#HsTO#q#CW#q#r#IS#r;'S#CW;'S;=`#IX<%lO#CW!R#IXO_!R!R#I[P;=`<%l#CW!Z#Id{jWOX#@[XZ#CWZ[#@[[^#CW^p#@[pq#CWqr#@[rs#CWsw#@[wx#CWx!Q#@[!Q![#@[![!^#@[!^!a#CW!a!c#@[!c!}#@[!}#O#@[#O#P#I_#P#S#@[#S#T#CW#T#o#@[#o#q#@[#q#r#LZ#r#y#@[#y#z#@[#z$f#@[$f$g#@[$gG|#@[G|#BY,m#BY#BZ#@[#BZ$IS,m$IS$I_#@[$I_$I|,m$I|$JO#@[$JO$JT,m$JT$JU#@[$JU$KV,m$KV$KW#@[$KW&FU,m&FU&FV#@[&FV;'S,m;'S;=`-b<%lO,m!Z#L`cjWOX#@[XZ#CWZ[#@[[^#CW^p#@[pq#CWqr#@[rs#CWsw#@[wx#CWx!^#@[!^!a#CW!a#S#@[#S#T#CW#T#q#@[#q#r#Mk#r;'S#@[;'S;=`$$p<%lO#@[!Z#Mr{jW_!ROX#@[XZ#CWZ[#@[[^#CW^p#@[pq#CWqr#@[rs#CWsw#@[wx#CWx!Q#@[!Q![#@[![!^#@[!^!a#CW!a!c#@[!c!}#@[!}#O#@[#O#P#I_#P#S#@[#S#T#CW#T#o#@[#o#q#@[#q#r$!i#r#y#@[#y#z#@[#z$f#@[$f$g#@[$gG|#@[G|#BY,m#BY#BZ#@[#BZ$IS,m$IS$I_#@[$I_$I|,m$I|$JO#@[$JO$JT,m$JT$JU#@[$JU$KV,m$KV$KW#@[$KW&FU,m&FU&FV#@[&FV;'S,m;'S;=`-b<%lO,m!Z$!ncjWOX#@[XZ#CWZ[#@[[^#CW^p#@[pq#CWqr#@[rs#CWsw#@[wx#CWx!^#@[!^!a#CW!a#S#@[#S#T#CW#T#q#@[#q#r$#y#r;'S#@[;'S;=`$$p<%lO#@[!Z$$QYjW_!ROX,mZ[,m^p,mqr,msw,mx!^,m!a#S,m#T;'S,m;'S;=`-b<%lO,m!Z$$sP;=`<%l#@[!V$${{gSOX#CWX^#CW^p#CWpq#CWqr$$vrs#CWsw$$vwx#CWx!P$$v!P!Q#CW!Q![$$v![!_$$v!_!a#CW!a!c$$v!c!}$$v!}#O$$v#O#P$'r#P#T$$v#T#o$$v#o#q$$v#q#r$.|#r#s$$v#s#y#CW#y#z#CW#z$f#CW$f$g$$v$gG|$$vG|#BY5p#BY#BZ$$v#BZ$IS5p$IS$I_$$v$I_$I|5p$I|$JO$$v$JO$JT5p$JT$JU$$v$JU$KV5p$KV$KW$$v$KW&FU5p&FU&FV$$v&FV;'S5p;'S;=`6b<%l?Ah5p?BY?Mn5p!V$'w{gSOX#CWX^#CW^p#CWpq#CWqr$$vrs#CWsw$$vwx#CWx!P$$v!P!Q#CW!Q![$$v![!_$$v!_!a#CW!a!c$$v!c!}$$v!}#O$$v#O#P$'r#P#T$$v#T#o$$v#o#q$$v#q#r$*n#r#s$$v#s#y#CW#y#z#CW#z$f#CW$f$g$$v$gG|$$vG|#BY5p#BY#BZ$$v#BZ$IS5p$IS$I_$$v$I_$I|5p$I|$JO$$v$JO$JT5p$JT$JU$$v$JU$KV5p$KV$KW$$v$KW&FU5p&FU&FV$$v&FV;'S5p;'S;=`6b<%l?Ah5p?BY?Mn5p!V$*scgSOq#CWqr$$vrs#CWsw$$vwx#CWx!P$$v!P!Q#CW!Q!_$$v!_!a#CW!a#q$$v#q#r$,O#r#s$$v#s$f#CW$f;'S$$v;'S;=`$1Q<%l?Ah$$v?Ah?BY#CW?BY?Mn$$v?MnO#CW!V$,V{gS_!ROX#CWX^#CW^p#CWpq#CWqr$$vrs#CWsw$$vwx#CWx!P$$v!P!Q#CW!Q![$$v![!_$$v!_!a#CW!a!c$$v!c!}$$v!}#O$$v#O#P$'r#P#T$$v#T#o$$v#o#q$$v#q#r$.|#r#s$$v#s#y#CW#y#z#CW#z$f#CW$f$g$$v$gG|$$vG|#BY5p#BY#BZ$$v#BZ$IS5p$IS$I_$$v$I_$I|5p$I|$JO$$v$JO$JT5p$JT$JU$$v$JU$KV5p$KV$KW$$v$KW&FU5p&FU&FV$$v&FV;'S5p;'S;=`6b<%l?Ah5p?BY?Mn5p!V$/RcgSOq#CWqr$$vrs#CWsw$$vwx#CWx!P$$v!P!Q#CW!Q!_$$v!_!a#CW!a#q$$v#q#r$0^#r#s$$v#s$f#CW$f;'S$$v;'S;=`$1Q<%l?Ah$$v?Ah?BY#CW?BY?Mn$$v?MnO#CW!V$0eXgS_!Rqr5psw5px!P5p!Q!_5p!a#s5p$f;'S5p;'S;=`6b<%l?Ah5p?BY?Mn5p!V$1TP;=`<%l$$v!_$1_!RgSjWOX#@[XZ#CWZ[#@[[^#CW^p#@[pq#CWqr#<zrs#CWsw#<zwx#CWx!P#<z!P!Q#@[!Q![#<z![!^#<z!^!_$$v!_!a#CW!a!c#<z!c!}#<z!}#O#<z#O#P$1W#P#S#<z#S#T$$v#T#o#<z#o#q#<z#q#r$4h#r#s#<z#s#y#@[#y#z#@[#z$f#@[$f$g#<z$gG|#<zG|#BY4^#BY#BZ#<z#BZ$IS4^$IS$I_#<z$I_$I|4^$I|$JO#<z$JO$JT4^$JT$JU#<z$JU$KV4^$KV$KW#<z$KW&FU4^&FU&FV#<z&FV;'S4^;'S;=`6h<%l?Ah4^?Ah?BY,m?BY?Mn4^?MnO,m!_$4okgSjWOX#@[XZ#CWZ[#@[[^#CW^p#@[pq#CWqr#<zrs#CWsw#<zwx#CWx!P#<z!P!Q#@[!Q!^#<z!^!_$$v!_!a#CW!a#S#<z#S#T$$v#T#q#<z#q#r$6d#r#s#<z#s$f#@[$f;'S#<z;'S;=`$=W<%l?Ah#<z?Ah?BY#@[?BY?Mn#<z?MnO#@[!_$6m!RgSjW_!ROX#@[XZ#CWZ[#@[[^#CW^p#@[pq#CWqr#<zrs#CWsw#<zwx#CWx!P#<z!P!Q#@[!Q![#<z![!^#<z!^!_$$v!_!a#CW!a!c#<z!c!}#<z!}#O#<z#O#P$1W#P#S#<z#S#T$$v#T#o#<z#o#q#<z#q#r$9v#r#s#<z#s#y#@[#y#z#@[#z$f#@[$f$g#<z$gG|#<zG|#BY4^#BY#BZ#<z#BZ$IS4^$IS$I_#<z$I_$I|4^$I|$JO#<z$JO$JT4^$JT$JU#<z$JU$KV4^$KV$KW#<z$KW&FU4^&FU&FV#<z&FV;'S4^;'S;=`6h<%l?Ah4^?Ah?BY,m?BY?Mn4^?MnO,m!_$9}kgSjWOX#@[XZ#CWZ[#@[[^#CW^p#@[pq#CWqr#<zrs#CWsw#<zwx#CWx!P#<z!P!Q#@[!Q!^#<z!^!_$$v!_!a#CW!a#S#<z#S#T$$v#T#q#<z#q#r$;r#r#s#<z#s$f#@[$f;'S#<z;'S;=`$=W<%l?Ah#<z?Ah?BY#@[?BY?Mn#<z?MnO#@[!_$;{cgSjW_!ROX,mZ[,m^p,mqr4^sw4^x!P4^!P!Q,m!Q!^4^!^!_5p!a#S4^#S#T5p#T#s4^#s$f,m$f;'S4^;'S;=`6h<%l?Ah4^?Ah?BY,m?BY?Mn4^?MnO,m!_$=ZP;=`<%l#<z",
	tokenizers: [
		scriptTokens,
		styleTokens,
		textareaTokens,
		tagStart,
		commentContent,
		singleBraceToken,
		0,
		1,
		2,
		3,
		4,
		5,
	],
	topRules: { Document: [0, 13] },
	dialects: { noMatch: 0, selfClosing: 491 },
	tokenPrec: 493,
});

function getAttrs(openTag, input) {
	let attrs = Object.create(null);
	for (let att of openTag.getChildren(Attribute)) {
		let name = att.getChild(AttributeName),
			value = att.getChild(AttributeValue) || att.getChild(UnquotedAttributeValue);
		if (name)
			attrs[input.read(name.from, name.to)] = !value
				? ''
				: value.type.id == AttributeValue
					? input.read(value.from + 1, value.to - 1)
					: input.read(value.from, value.to);
	}
	return attrs;
}

function findTagName(openTag, input) {
	let tagNameNode = openTag.getChild(TagName);
	return tagNameNode ? input.read(tagNameNode.from, tagNameNode.to) : ' ';
}

function maybeNest(node, input, tags) {
	let attrs;
	for (let tag of tags) {
		if (!tag.attrs || tag.attrs(attrs || (attrs = getAttrs(node.node.parent.firstChild, input))))
			return { parser: tag.parser };
	}
	return null;
}

// tags?: {
//   tag: string,
//   attrs?: ({[attr: string]: string}) => boolean,
//   parser: Parser
// }[]
// attributes?: {
//   name: string,
//   tagName?: string,
//   parser: Parser
// }[]

function configureNesting(tags = [], attributes = []) {
	let script = [],
		style = [],
		textarea = [],
		other = [];
	for (let tag of tags) {
		let array =
			tag.tag == 'script'
				? script
				: tag.tag == 'style'
					? style
					: tag.tag == 'textarea'
						? textarea
						: other;
		array.push(tag);
	}
	let attrs = attributes.length ? Object.create(null) : null;
	for (let attr of attributes) (attrs[attr.name] || (attrs[attr.name] = [])).push(attr);

	return parseMixed((node, input) => {
		let id = node.type.id;
		if (id == ScriptText) return maybeNest(node, input, script);
		if (id == StyleText) return maybeNest(node, input, style);
		if (id == TextareaText) return maybeNest(node, input, textarea);

		if (id == OpenTag && other.length) {
			let n = node.node,
				tagName = findTagName(n, input),
				attrs;
			for (let tag of other) {
				if (
					tag.tag == tagName &&
					(!tag.attrs || tag.attrs(attrs || (attrs = getAttrs(n, input))))
				) {
					let close = n.parent.lastChild;
					return {
						parser: tag.parser,
						overlay: [{ from: node.to, to: close.type.id == CloseTag ? close.from : n.parent.to }],
					};
				}
			}
		}

		if (attrs && id == Attribute) {
			let n = node.node,
				nameNode;
			if ((nameNode = n.firstChild)) {
				let matches = attrs[input.read(nameNode.from, nameNode.to)];
				if (matches)
					for (let attr of matches) {
						if (attr.tagName && attr.tagName != findTagName(n.parent, input)) continue;
						let value = n.lastChild;
						if (value.type.id == AttributeValue)
							return { parser: attr.parser, overlay: [{ from: value.from + 1, to: value.to - 1 }] };
						else if (value.type.id == UnquotedAttributeValue)
							return { parser: attr.parser, overlay: [{ from: value.from, to: value.to }] };
					}
			}
		}
		return null;
	});
}

const Targets = ['_blank', '_self', '_top', '_parent'];
const Charsets = ['ascii', 'utf-8', 'utf-16', 'latin1'];
const Methods = ['get', 'post', 'put', 'delete'];
const Encs = ['application/x-www-form-urlencoded', 'multipart/form-data', 'text/plain'];
const Bool = ['true', 'false'];
const S = {}; // Empty tag spec
const Tags = {
    a: {
        attrs: {
            href: null,
            ping: null,
            type: null,
            media: null,
            target: Targets,
            hreflang: null,
        },
    },
    abbr: S,
    address: S,
    area: {
        attrs: {
            alt: null,
            coords: null,
            href: null,
            target: null,
            ping: null,
            media: null,
            hreflang: null,
            type: null,
            shape: ['default', 'rect', 'circle', 'poly'],
        },
    },
    article: S,
    aside: S,
    audio: {
        attrs: {
            src: null,
            mediagroup: null,
            crossorigin: ['anonymous', 'use-credentials'],
            preload: ['none', 'metadata', 'auto'],
            autoplay: ['autoplay'],
            loop: ['loop'],
            controls: ['controls'],
        },
    },
    b: S,
    base: { attrs: { href: null, target: Targets } },
    bdi: S,
    bdo: S,
    blockquote: { attrs: { cite: null } },
    body: S,
    br: S,
    button: {
        attrs: {
            form: null,
            formaction: null,
            name: null,
            value: null,
            autofocus: ['autofocus'],
            disabled: ['disabled'],
            formenctype: Encs,
            formmethod: Methods,
            formnovalidate: ['novalidate'],
            formtarget: Targets,
            type: ['submit', 'reset', 'button'],
        },
    },
    canvas: { attrs: { width: null, height: null } },
    caption: S,
    center: S,
    cite: S,
    code: S,
    col: { attrs: { span: null } },
    colgroup: { attrs: { span: null } },
    command: {
        attrs: {
            type: ['command', 'checkbox', 'radio'],
            label: null,
            icon: null,
            radiogroup: null,
            command: null,
            title: null,
            disabled: ['disabled'],
            checked: ['checked'],
        },
    },
    data: { attrs: { value: null } },
    datagrid: { attrs: { disabled: ['disabled'], multiple: ['multiple'] } },
    datalist: { attrs: { data: null } },
    dd: S,
    del: { attrs: { cite: null, datetime: null } },
    details: { attrs: { open: ['open'] } },
    dfn: S,
    div: S,
    dl: S,
    dt: S,
    em: S,
    embed: { attrs: { src: null, type: null, width: null, height: null } },
    eventsource: { attrs: { src: null } },
    fieldset: { attrs: { disabled: ['disabled'], form: null, name: null } },
    figcaption: S,
    figure: S,
    footer: S,
    form: {
        attrs: {
            action: null,
            name: null,
            'accept-charset': Charsets,
            autocomplete: ['on', 'off'],
            enctype: Encs,
            method: Methods,
            novalidate: ['novalidate'],
            target: Targets,
        },
    },
    h1: S,
    h2: S,
    h3: S,
    h4: S,
    h5: S,
    h6: S,
    head: {
        children: ['title', 'base', 'link', 'style', 'meta', 'script', 'noscript', 'command'],
    },
    header: S,
    hgroup: S,
    hr: S,
    html: {
        attrs: { manifest: null },
    },
    i: S,
    iframe: {
        attrs: {
            src: null,
            srcdoc: null,
            name: null,
            width: null,
            height: null,
            sandbox: ['allow-top-navigation', 'allow-same-origin', 'allow-forms', 'allow-scripts'],
            seamless: ['seamless'],
        },
    },
    img: {
        attrs: {
            alt: null,
            src: null,
            ismap: null,
            usemap: null,
            width: null,
            height: null,
            crossorigin: ['anonymous', 'use-credentials'],
        },
    },
    input: {
        attrs: {
            alt: null,
            dirname: null,
            form: null,
            formaction: null,
            height: null,
            list: null,
            max: null,
            maxlength: null,
            min: null,
            name: null,
            pattern: null,
            placeholder: null,
            size: null,
            src: null,
            step: null,
            value: null,
            width: null,
            accept: ['audio/*', 'video/*', 'image/*'],
            autocomplete: ['on', 'off'],
            autofocus: ['autofocus'],
            checked: ['checked'],
            disabled: ['disabled'],
            formenctype: Encs,
            formmethod: Methods,
            formnovalidate: ['novalidate'],
            formtarget: Targets,
            multiple: ['multiple'],
            readonly: ['readonly'],
            required: ['required'],
            type: [
                'hidden',
                'text',
                'search',
                'tel',
                'url',
                'email',
                'password',
                'datetime',
                'date',
                'month',
                'week',
                'time',
                'datetime-local',
                'number',
                'range',
                'color',
                'checkbox',
                'radio',
                'file',
                'submit',
                'image',
                'reset',
                'button',
            ],
        },
    },
    ins: { attrs: { cite: null, datetime: null } },
    kbd: S,
    keygen: {
        attrs: {
            challenge: null,
            form: null,
            name: null,
            autofocus: ['autofocus'],
            disabled: ['disabled'],
            keytype: ['RSA'],
        },
    },
    label: { attrs: { for: null, form: null } },
    legend: S,
    li: { attrs: { value: null } },
    link: {
        attrs: {
            href: null,
            type: null,
            hreflang: null,
            media: null,
            sizes: ['all', '16x16', '16x16 32x32', '16x16 32x32 64x64'],
        },
    },
    map: { attrs: { name: null } },
    mark: S,
    menu: { attrs: { label: null, type: ['list', 'context', 'toolbar'] } },
    meta: {
        attrs: {
            content: null,
            charset: Charsets,
            name: ['viewport', 'application-name', 'author', 'description', 'generator', 'keywords'],
            'http-equiv': ['content-language', 'content-type', 'default-style', 'refresh'],
        },
    },
    meter: { attrs: { value: null, min: null, low: null, high: null, max: null, optimum: null } },
    nav: S,
    noscript: S,
    object: {
        attrs: {
            data: null,
            type: null,
            name: null,
            usemap: null,
            form: null,
            width: null,
            height: null,
            typemustmatch: ['typemustmatch'],
        },
    },
    ol: {
        attrs: { reversed: ['reversed'], start: null, type: ['1', 'a', 'A', 'i', 'I'] },
        children: ['li', 'script', 'template', 'ul', 'ol'],
    },
    optgroup: { attrs: { disabled: ['disabled'], label: null } },
    option: { attrs: { disabled: ['disabled'], label: null, selected: ['selected'], value: null } },
    output: { attrs: { for: null, form: null, name: null } },
    p: S,
    param: { attrs: { name: null, value: null } },
    pre: S,
    progress: { attrs: { value: null, max: null } },
    q: { attrs: { cite: null } },
    rp: S,
    rt: S,
    ruby: S,
    samp: S,
    script: {
        attrs: {
            type: ['text/javascript'],
            src: null,
            async: ['async'],
            defer: ['defer'],
            charset: Charsets,
        },
    },
    section: S,
    select: {
        attrs: {
            form: null,
            name: null,
            size: null,
            autofocus: ['autofocus'],
            disabled: ['disabled'],
            multiple: ['multiple'],
        },
    },
    slot: { attrs: { name: null } },
    small: S,
    source: { attrs: { src: null, type: null, media: null } },
    span: S,
    strong: S,
    style: {
        attrs: {
            type: ['text/css'],
            media: null,
            scoped: null,
        },
    },
    sub: S,
    summary: S,
    sup: S,
    table: S,
    tbody: S,
    td: { attrs: { colspan: null, rowspan: null, headers: null } },
    template: S,
    textarea: {
        attrs: {
            dirname: null,
            form: null,
            maxlength: null,
            name: null,
            placeholder: null,
            rows: null,
            cols: null,
            autofocus: ['autofocus'],
            disabled: ['disabled'],
            readonly: ['readonly'],
            required: ['required'],
            wrap: ['soft', 'hard'],
        },
    },
    tfoot: S,
    th: {
        attrs: {
            colspan: null,
            rowspan: null,
            headers: null,
            scope: ['row', 'col', 'rowgroup', 'colgroup'],
        },
    },
    thead: S,
    time: { attrs: { datetime: null } },
    title: S,
    tr: S,
    track: {
        attrs: {
            src: null,
            label: null,
            default: null,
            kind: ['subtitles', 'captions', 'descriptions', 'chapters', 'metadata'],
            srclang: null,
        },
    },
    ul: { children: ['li', 'script', 'template', 'ul', 'ol'] },
    var: S,
    video: {
        attrs: {
            src: null,
            poster: null,
            width: null,
            height: null,
            crossorigin: ['anonymous', 'use-credentials'],
            preload: ['auto', 'metadata', 'none'],
            autoplay: ['autoplay'],
            mediagroup: ['movie'],
            muted: ['muted'],
            controls: ['controls'],
        },
    },
    wbr: S,
};
const GlobalAttrs = {
    accesskey: null,
    class: null,
    contenteditable: Bool,
    contextmenu: null,
    dir: ['ltr', 'rtl', 'auto'],
    draggable: ['true', 'false', 'auto'],
    dropzone: ['copy', 'move', 'link', 'string:', 'file:'],
    hidden: ['hidden'],
    id: null,
    inert: ['inert'],
    itemid: null,
    itemprop: null,
    itemref: null,
    itemscope: ['itemscope'],
    itemtype: null,
    lang: [
        'ar',
        'bn',
        'de',
        'en-GB',
        'en-US',
        'es',
        'fr',
        'hi',
        'id',
        'ja',
        'pa',
        'pt',
        'ru',
        'tr',
        'zh',
    ],
    spellcheck: Bool,
    autocorrect: Bool,
    autocapitalize: Bool,
    style: null,
    tabindex: null,
    title: null,
    translate: ['yes', 'no'],
    rel: [
        'stylesheet',
        'alternate',
        'author',
        'bookmark',
        'help',
        'license',
        'next',
        'nofollow',
        'noreferrer',
        'prefetch',
        'prev',
        'search',
        'tag',
    ],
    role: /*@__PURE__*/'alert application article banner button cell checkbox complementary contentinfo dialog document feed figure form grid gridcell heading img list listbox listitem main navigation region row rowgroup search switch tab table tabpanel textbox timer'.split(' '),
    'aria-activedescendant': null,
    'aria-atomic': Bool,
    'aria-autocomplete': ['inline', 'list', 'both', 'none'],
    'aria-busy': Bool,
    'aria-checked': ['true', 'false', 'mixed', 'undefined'],
    'aria-controls': null,
    'aria-describedby': null,
    'aria-disabled': Bool,
    'aria-dropeffect': null,
    'aria-expanded': ['true', 'false', 'undefined'],
    'aria-flowto': null,
    'aria-grabbed': ['true', 'false', 'undefined'],
    'aria-haspopup': Bool,
    'aria-hidden': Bool,
    'aria-invalid': ['true', 'false', 'grammar', 'spelling'],
    'aria-label': null,
    'aria-labelledby': null,
    'aria-level': null,
    'aria-live': ['off', 'polite', 'assertive'],
    'aria-multiline': Bool,
    'aria-multiselectable': Bool,
    'aria-owns': null,
    'aria-posinset': null,
    'aria-pressed': ['true', 'false', 'mixed', 'undefined'],
    'aria-readonly': Bool,
    'aria-relevant': null,
    'aria-required': Bool,
    'aria-selected': ['true', 'false', 'undefined'],
    'aria-setsize': null,
    'aria-sort': ['ascending', 'descending', 'none', 'other'],
    'aria-valuemax': null,
    'aria-valuemin': null,
    'aria-valuenow': null,
    'aria-valuetext': null,
};
const eventAttributes = /*@__PURE__*/('beforeunload copy cut dragstart dragover dragleave dragenter dragend ' +
    'drag paste focus blur change click load mousedown mouseenter mouseleave ' +
    'mouseup keydown keyup resize scroll unload')
    .split(' ')
    .map((n) => 'on' + n);
for (let a of eventAttributes)
    GlobalAttrs[a] = null;
class Schema {
    constructor(extraTags, extraAttrs) {
        this.tags = Object.assign(Object.assign({}, Tags), extraTags);
        this.globalAttrs = Object.assign(Object.assign({}, GlobalAttrs), extraAttrs);
        this.allTags = Object.keys(this.tags);
        this.globalAttrNames = Object.keys(this.globalAttrs);
    }
}
Schema.default = /*@__PURE__*/new Schema();
function elementName(doc, tree, max = doc.length) {
    if (!tree)
        return '';
    let tag = tree.firstChild;
    let name = tag && tag.getChild('TagName');
    return name ? doc.sliceString(name.from, Math.min(name.to, max)) : '';
}
function findParentElement(tree, skip = false) {
    for (let cur = tree.parent; cur; cur = cur.parent)
        if (cur.name == 'Element') {
            if (skip)
                skip = false;
            else
                return cur;
        }
    return null;
}
function allowedChildren(doc, tree, schema) {
    let parentInfo = schema.tags[elementName(doc, findParentElement(tree, true))];
    return (parentInfo === null || parentInfo === void 0 ? void 0 : parentInfo.children) || schema.allTags;
}
function openTags(doc, tree) {
    let open = [];
    for (let parent = tree; (parent = findParentElement(parent));) {
        let tagName = elementName(doc, parent);
        if (tagName && parent.lastChild.name == 'CloseTag')
            break;
        if (tagName &&
            open.indexOf(tagName) < 0 &&
            (tree.name == 'EndTag' || tree.from >= parent.firstChild.to))
            open.push(tagName);
    }
    return open;
}
const identifier = /^[:\-\.\w\u00b7-\uffff]*$/;
function completeTag(state, schema, tree, from, to) {
    let end = /\s*>/.test(state.sliceDoc(to, to + 5)) ? '' : '>';
    return {
        from,
        to,
        options: allowedChildren(state.doc, tree, schema)
            .map((tagName) => ({ label: tagName, type: 'type' }))
            .concat(openTags(state.doc, tree).map((tag, i) => ({
            label: '/' + tag,
            apply: '/' + tag + end,
            type: 'type',
            boost: 99 - i,
        }))),
        validFor: /^\/?[:\-\.\w\u00b7-\uffff]*$/,
    };
}
function completeCloseTag(state, tree, from, to) {
    let end = /\s*>/.test(state.sliceDoc(to, to + 5)) ? '' : '>';
    return {
        from,
        to,
        options: openTags(state.doc, tree).map((tag, i) => ({
            label: tag,
            apply: tag + end,
            type: 'type',
            boost: 99 - i,
        })),
        validFor: identifier,
    };
}
function completeStartTag(state, schema, tree, pos) {
    let options = [], level = 0;
    for (let tagName of allowedChildren(state.doc, tree, schema))
        options.push({ label: '<' + tagName, type: 'type' });
    for (let open of openTags(state.doc, tree))
        options.push({ label: '</' + open + '>', type: 'type', boost: 99 - level++ });
    return { from: pos, to: pos, options, validFor: /^<\/?[:\-\.\w\u00b7-\uffff]*$/ };
}
function completeAttrName(state, schema, tree, from, to) {
    let elt = findParentElement(tree), info = elt ? schema.tags[elementName(state.doc, elt)] : null;
    let localAttrs = info && info.attrs ? Object.keys(info.attrs) : [];
    let names = info && info.globalAttrs === false
        ? localAttrs
        : localAttrs.length
            ? localAttrs.concat(schema.globalAttrNames)
            : schema.globalAttrNames;
    return {
        from,
        to,
        options: names.map((attrName) => ({ label: attrName, type: 'property' })),
        validFor: identifier,
    };
}
function completeAttrValue(state, schema, tree, from, to) {
    var _a;
    let nameNode = (_a = tree.parent) === null || _a === void 0 ? void 0 : _a.getChild('AttributeName');
    let options = [], token = undefined;
    if (nameNode) {
        let attrName = state.sliceDoc(nameNode.from, nameNode.to);
        let attrs = schema.globalAttrs[attrName];
        if (!attrs) {
            let elt = findParentElement(tree), info = elt ? schema.tags[elementName(state.doc, elt)] : null;
            attrs = (info === null || info === void 0 ? void 0 : info.attrs) && info.attrs[attrName];
        }
        if (attrs) {
            let base = state.sliceDoc(from, to).toLowerCase(), quoteStart = '"', quoteEnd = '"';
            if (/^['"]/.test(base)) {
                token = base[0] == '"' ? /^[^"]*$/ : /^[^']*$/;
                quoteStart = '';
                quoteEnd = state.sliceDoc(to, to + 1) == base[0] ? '' : base[0];
                base = base.slice(1);
                from++;
            }
            else {
                token = /^[^\s<>='"]*$/;
            }
            for (let value of attrs)
                options.push({ label: value, apply: quoteStart + value + quoteEnd, type: 'constant' });
        }
    }
    return { from, to, options, validFor: token };
}
function htmlCompletionFor(schema, context) {
    let { state, pos } = context, around = syntaxTree(state).resolveInner(pos), tree = around.resolve(pos, -1);
    for (let scan = pos, before; around == tree && (before = tree.childBefore(scan));) {
        let last = before.lastChild;
        if (!last || !last.type.isError || last.from < last.to)
            break;
        around = tree = before;
        scan = last.from;
    }
    if (tree.name == 'TagName') {
        return tree.parent && /CloseTag$/.test(tree.parent.name)
            ? completeCloseTag(state, tree, tree.from, pos)
            : completeTag(state, schema, tree, tree.from, pos);
    }
    else if (tree.name == 'StartTag') {
        return completeTag(state, schema, tree, pos, pos);
    }
    else if (tree.name == 'StartCloseTag' || tree.name == 'IncompleteCloseTag') {
        return completeCloseTag(state, tree, pos, pos);
    }
    else if ((context.explicit && (tree.name == 'OpenTag' || tree.name == 'SelfClosingTag')) ||
        tree.name == 'AttributeName') {
        return completeAttrName(state, schema, tree, tree.name == 'AttributeName' ? tree.from : pos, pos);
    }
    else if (tree.name == 'Is' ||
        tree.name == 'AttributeValue' ||
        tree.name == 'UnquotedAttributeValue') {
        return completeAttrValue(state, schema, tree, tree.name == 'Is' ? pos : tree.from, pos);
    }
    else if (context.explicit &&
        (around.name == 'Element' || around.name == 'Text' || around.name == 'Document')) {
        return completeStartTag(state, schema, tree, pos);
    }
    else {
        return null;
    }
}
/**
HTML tag completion. Opens and closes tags and attributes in a
context-aware way.
*/
function htmlCompletionSource(context) {
    return htmlCompletionFor(Schema.default, context);
}
/**
Create a completion source for HTML extended with additional tags
or attributes.
*/
function htmlCompletionSourceWith(config) {
    let { extraTags, extraGlobalAttributes: extraAttrs } = config;
    let schema = extraAttrs || extraTags ? new Schema(extraTags, extraAttrs) : Schema.default;
    return (context) => htmlCompletionFor(schema, context);
}

// import {parser, configureNesting} from "@lezer/html"
const defaultNesting = [
    {
        tag: 'script',
        attrs: (attrs) => attrs.type == 'text/typescript' || attrs.lang == 'ts',
        parser: typescriptLanguage.parser,
    },
    {
        tag: 'script',
        attrs: (attrs) => attrs.type == 'text/babel' || attrs.type == 'text/jsx',
        parser: jsxLanguage.parser,
    },
    {
        tag: 'script',
        attrs: (attrs) => attrs.type == 'text/typescript-jsx',
        parser: tsxLanguage.parser,
    },
    {
        tag: 'script',
        attrs(attrs) {
            return (!attrs.type ||
                /^(?:text|application)\/(?:x-)?(?:java|ecma)script$|^module$|^$/i.test(attrs.type));
        },
        parser: javascriptLanguage.parser,
    },
    {
        tag: 'style',
        attrs(attrs) {
            return ((!attrs.lang || attrs.lang == 'css') &&
                (!attrs.type || /^(text\/)?(x-)?(stylesheet|css)$/i.test(attrs.type)));
        },
        parser: cssLanguage.parser,
    },
];
const defaultAttrs = /*@__PURE__*/[
    { name: 'style', parser: /*@__PURE__*/cssLanguage.parser.configure({ top: 'Styles' }) },
].concat(/*@__PURE__*/eventAttributes.map((name) => ({ name, parser: javascriptLanguage.parser })));
/**
A language provider based on the [Lezer HTML
parser](https://github.com/lezer-parser/html), extended with the
JavaScript and CSS parsers to parse the content of `<script>` and
`<style>` tags.
*/
const htmlLanguage = /*@__PURE__*/LRLanguage.define({
    name: 'html',
    parser: /*@__PURE__*/parser.configure({
        props: [
            /*@__PURE__*/indentNodeProp.add({
                Element(context) {
                    let after = /^(\s*)(<\/)?/.exec(context.textAfter);
                    if (context.node.to <= context.pos + after[0].length)
                        return context.continue();
                    return context.lineIndent(context.node.from) + (after[2] ? 0 : context.unit);
                },
                'OpenTag CloseTag SelfClosingTag'(context) {
                    return context.column(context.node.from) + context.unit;
                },
                Document(context) {
                    if (context.pos + /\s*/.exec(context.textAfter)[0].length < context.node.to)
                        return context.continue();
                    let endElt = null, close;
                    for (let cur = context.node;;) {
                        let last = cur.lastChild;
                        if (!last || last.name != 'Element' || last.to != cur.to)
                            break;
                        endElt = cur = last;
                    }
                    if (endElt &&
                        !((close = endElt.lastChild) &&
                            (close.name == 'CloseTag' || close.name == 'SelfClosingTag')))
                        return context.lineIndent(endElt.from) + context.unit;
                    return null;
                },
            }),
            /*@__PURE__*/foldNodeProp.add({
                Element(node) {
                    let first = node.firstChild, last = node.lastChild;
                    if (!first || first.name != 'OpenTag')
                        return null;
                    return { from: first.to, to: last.name == 'CloseTag' ? last.from : node.to };
                },
            }),
        ],
        wrap: /*@__PURE__*/configureNesting(defaultNesting, defaultAttrs),
    }),
    languageData: {
        commentTokens: { block: { open: '<!--', close: '-->' } },
        indentOnInput: /^\s*<\/\w+\W$/,
        wordChars: '-._',
    },
});
/**
Language support for HTML, including
[`htmlCompletion`](https://codemirror.net/6/docs/ref/#lang-html.htmlCompletion) and JavaScript and
CSS support extensions.
*/
function html(config = {}) {
    let dialect = '', wrap;
    if (config.matchClosingTags === false)
        dialect = 'noMatch';
    if (config.selfClosingTags === true)
        dialect = (dialect ? dialect + ' ' : '') + 'selfClosing';
    if ((config.nestedLanguages && config.nestedLanguages.length) ||
        (config.nestedAttributes && config.nestedAttributes.length))
        wrap = configureNesting((config.nestedLanguages || []).concat(defaultNesting), (config.nestedAttributes || []).concat(defaultAttrs));
    let lang = wrap || dialect ? htmlLanguage.configure({ dialect, wrap }) : htmlLanguage;
    return new LanguageSupport(lang, [
        htmlLanguage.data.of({ autocomplete: htmlCompletionSourceWith(config) }),
        config.autoCloseTags !== false ? autoCloseTags : [],
        javascript().support,
        css().support,
    ]);
}
/**
Extension that will automatically insert close tags when a `>` or
`/` is typed.
*/
const autoCloseTags = /*@__PURE__*/EditorView.inputHandler.of((view, from, to, text) => {
    if (view.composing ||
        view.state.readOnly ||
        from != to ||
        (text != '>' && text != '/') ||
        !htmlLanguage.isActiveAt(view.state, from, -1))
        return false;
    let { state } = view;
    let changes = state.changeByRange((range) => {
        var _a, _b, _c;
        let { head } = range, around = syntaxTree(state).resolveInner(head, -1), name;
        if (around.name == 'TagName' || around.name == 'StartTag')
            around = around.parent;
        if (text == '>' && around.name == 'OpenTag') {
            if (((_b = (_a = around.parent) === null || _a === void 0 ? void 0 : _a.lastChild) === null || _b === void 0 ? void 0 : _b.name) != 'CloseTag' &&
                (name = elementName(state.doc, around.parent, head))) {
                let hasRightBracket = view.state.doc.sliceString(head, head + 1) === '>';
                let insert = `${hasRightBracket ? '' : '>'}</${name}>`;
                return {
                    range: EditorSelection.cursor(head + 1),
                    changes: { from: head + (hasRightBracket ? 1 : 0), insert },
                };
            }
        }
        else if (text == '/' && around.name == 'OpenTag') {
            let empty = around.parent, base = empty === null || empty === void 0 ? void 0 : empty.parent;
            if (empty.from == head - 1 &&
                ((_c = base.lastChild) === null || _c === void 0 ? void 0 : _c.name) != 'CloseTag' &&
                (name = elementName(state.doc, base, head))) {
                let hasRightBracket = view.state.doc.sliceString(head, head + 1) === '>';
                let insert = `/${name}${hasRightBracket ? '' : '>'}`;
                let pos = head + insert.length + (hasRightBracket ? 1 : 0);
                return { range: EditorSelection.cursor(pos), changes: { from: head, insert } };
            }
        }
        return { range };
    });
    if (changes.changes.empty)
        return false;
    view.dispatch(changes, { userEvent: 'input.type', scrollIntoView: true });
    return true;
});

export { autoCloseTags, html, htmlCompletionSource, htmlCompletionSourceWith, htmlLanguage };
